import { PageType, type ContentContainer, type Config, ProductType, WidgetType } from "../core/types";
import { Logger } from "../core/Logger";
import type { ITrackingService } from "./ITrackingService";

type DataLayerEvent = {
    event: string;
    [key: string]: string | number | undefined;
}

type VehicleConfig = {
    vin?: string;
    price?: number;
    year?: number;
    make?: string;
    model?: string;
}

export class GoogleAnalyticsService implements ITrackingService {

    constructor(
        private config: Config,
        private isTestMode: boolean = false
    ) {}

    initialize(): void {
        if (typeof window !== 'undefined') {
            window.dataLayer = window.dataLayer || [];
        } else {
            Logger.warn('[GoogleAnalyticsService] window is undefined, dataLayer not initialized');
        }

        if (this.config.tracking?.ga4MeasurementId) {
            this.pushEvent({
                event: 'client_ga4_config',
                clientGa4Id: this.config.tracking.ga4MeasurementId,
                event_owner: this.getEventOwner(),
            });
        }
    }

    private getEventOwner(): string {
        return 'selectfi';
    }

    private getElementColor(container: ContentContainer): string {
        return container.productType === ProductType.CALCULATOR
            ? this.config.styles.calculator.backgroundColor
            : this.config.styles.form.backgroundColor;
    }

    private getElementText(container: ContentContainer): string {
        if (container.widgetType === WidgetType.BANNER) {
            if (container.productType === ProductType.CALCULATOR) {
                return this.config.images.calculatorBannerUrl; // TODO: should this be alt text rather than URL?
            } else {
                return this.config.images.formBannerUrl; // TODO: should this be alt text rather than URL?
            }
        } else if (container.productType === ProductType.CALCULATOR) {
            return this.config.copy.calculator.primaryText + " | " + this.config.copy.calculator.secondaryText;
        } else {
            return this.config.copy.form.primaryText + " | " + this.config.copy.form.secondaryText;
        }
    }

    private getPageType(container: ContentContainer): string {
        const pageTypeMap: Record<PageType, string> = {
            [PageType.VDP]: 'item',
            [PageType.SRP]: 'itemlist',
            [PageType.FINANCE]: 'finance'
        };
        return pageTypeMap[container.pageType] || 'unknown';
    }

    private getProductName(container: ContentContainer): string {
        const productNameMap: Record<ProductType, string> = {
            [ProductType.SCREENING]: 'selectfi_application',
            [ProductType.FULL]: 'selectfi_application',
            [ProductType.CALCULATOR]: 'selectfi_calculator'
        };
        return productNameMap[container.productType];
    }

    private getFormName(container: ContentContainer): string {
        const formNameMap: Record<ProductType, string> = {
            [ProductType.SCREENING]: 'selectfi_screening_app',
            [ProductType.FULL]: 'selectfi_full_credit_app',
            [ProductType.CALCULATOR]: 'selectfi_calculator'
        };
        return formNameMap[container.productType];
    }

    private getElementType(container: ContentContainer): string {
        const typeMap: Record<WidgetType, string> = {
            [WidgetType.BANNER]: 'banner',
            [WidgetType.CTA]: 'popup',
            [WidgetType.EMBED]: 'form'
        };
        return typeMap[container.widgetType];
    }

    private getElementSubType(container: ContentContainer): string {
        const subTypeMap: Record<WidgetType, string> = {
            [WidgetType.BANNER]: 'cta_button',
            [WidgetType.CTA]: 'cta_button',
            [WidgetType.EMBED]: 'content'
        };
        return subTypeMap[container.widgetType];
    }

    private getVehicleConfig(container: ContentContainer): VehicleConfig {
        // TODO: fetch from asc_dataLayer if available (asc_dataLayer[meta.indexPosition - 1])
        const config: VehicleConfig = {};

        if (container.vin) {
            config.vin = container.vin;
        }

        if (container.vehiclePrice) {
            config.price = container.vehiclePrice;
        }

        if (container.vehicleYear) {
            config.year = container.vehicleYear;
        }

        if (container.vehicleMake) {
            config.make = container.vehicleMake;
        }

        if (container.vehicleModel) {
            config.model = container.vehicleModel;
        }

        return config;
    }

    private pushEvent(event: DataLayerEvent): void {
        if (this.isTestMode) {
            Logger.debug('[GoogleAnalyticsService - Test Mode] Event not fired:', event);
            return;
        }

        try {
            window.dataLayer!.push(event);
            Logger.debug('[GoogleAnalyticsService] Event pushed:', event);
        } catch (error) {
            Logger.error('[GoogleAnalyticsService] Failed to push event:', error);
        }
    }

    trackOpen(container: ContentContainer): void {
        const vehicleConfig = this.getVehicleConfig(container);
        const isPopup = this.getElementType(container) !== 'form';

        this.pushEvent({
            event: 'asc_cta_interaction',
            event_owner: this.getEventOwner(),
            page_type: this.getPageType(container),
            element_text: this.getElementText(container),
            element_color: this.getElementColor(container),
            element_order: undefined,
            element_type: this.getElementType(container),
            element_subtype: this.getElementSubType(container),
            event_action: isPopup ? 'click' : 'view',
            event_action_result: isPopup ? 'popup' : 'shown',
            link_url: window.location.href,
            product_name: this.getProductName(container),
            affiliation: undefined,
            item_id: vehicleConfig.vin,
            item_number: undefined,
            item_price: vehicleConfig.price,
            item_condition: undefined,
            item_year: vehicleConfig.year,
            item_make: vehicleConfig.make,
            item_model: vehicleConfig.model,
            item_variant: undefined,
            item_color: undefined,
            item_type: undefined,
            item_category: undefined,
            item_inventory_date: undefined,
            item_location: container.dealerName, // will only fill for multi-dealer setups
        });
    }

    trackClose(container: ContentContainer): void {
        const vehicleConfig = this.getVehicleConfig(container);

        this.pushEvent({
            event: 'asc_cta_interaction',
            event_owner: this.getEventOwner(),
            page_type: this.getPageType(container),
            element_text: this.getElementText(container),
            element_color: this.getElementColor(container),
            element_order: undefined,
            element_type: this.getElementType(container),
            element_subtype: this.getElementSubType(container),
            event_action: 'click',
            event_action_result: 'close',
            link_url: window.location.href,
            product_name: this.getProductName(container),
            affiliation: undefined,
            item_id: vehicleConfig.vin,
            item_number: undefined,
            item_price: vehicleConfig.price,
            item_condition: undefined,
            item_year: vehicleConfig.year,
            item_make: vehicleConfig.make,
            item_model: vehicleConfig.model,
            item_variant: undefined,
            item_color: undefined,
            item_type: undefined,
            item_category: undefined,
            item_location: container.dealerName, // will only fill for multi-dealer setups
        });
    }

    trackFieldInteraction(container: ContentContainer, fieldName: string, fieldValue?: string): void {
        const vehicleConfig = this.getVehicleConfig(container);

        this.pushEvent({
            event: 'asc_form_engagement',
            event_owner: this.getEventOwner(),
            page_type: this.getPageType(container),
            comm_type: 'form',
            comm_status: 'engage',
            element_text: fieldName,
            element_value: fieldValue,
            department: 'sales',
            form_name: this.getFormName(container),
            form_type: 'finance/credit',
            product_name: this.getProductName(container),
            item_id: vehicleConfig.vin,
            item_number: undefined,
            item_price: vehicleConfig.price,
            item_condition: undefined,
            item_year: vehicleConfig.year,
            item_make: vehicleConfig.make,
            item_model: vehicleConfig.model,
            item_variant: undefined,
            item_color: undefined,
            item_type: undefined,
            item_category: undefined,
            item_inventory_date: undefined,
            item_location: container.dealerName, // will only fill for multi-dealer setups
        });
    }

    trackSubmit(container: ContentContainer): void {
        const vehicleConfig = this.getVehicleConfig(container);

        const baseEvent = {
            event_owner: this.getEventOwner(),
            page_type: this.getPageType(container),
            comm_type: 'form',
            comm_outcome: 'opportunity',
            element_text: this.getElementText(container),
            department: 'sales',
            form_name: this.getFormName(container),
            form_type: 'finance/credit',
            product_name: this.getProductName(container),
            item_id: vehicleConfig.vin,
            item_number: undefined,
            item_price: vehicleConfig.price,
            item_condition: undefined,
            item_year: vehicleConfig.year,
            item_make: vehicleConfig.make,
            item_model: vehicleConfig.model,
            item_variant: undefined,
            item_color: undefined,
            item_type: undefined,
            item_category: undefined,
            item_inventory_date: undefined,
            item_location: container.dealerName, // will only fill for multi-dealer setups
        };

        this.pushEvent({
            event: 'asc_form_submission',
            ...baseEvent
        });

        this.pushEvent({
            event: 'asc_form_submission_sales',
            ...baseEvent
        });
    }

    trackContinueToCreditApp(container: ContentContainer): void {
        const vehicleConfig = this.getVehicleConfig(container);

        this.pushEvent({
            event: 'asc_cta_interaction',
            event_owner: this.getEventOwner(),
            page_type: this.getPageType(container),
            element_text: this.getElementText(container),
            element_color: this.getElementColor(container),
            element_order: undefined,
            element_type: this.getElementType(container),
            element_subtype: this.getElementSubType(container),
            event_action: 'click', // Even for embeded widgets, this is a click to continue
            event_action_result: 'next_step', // "next_step" indicates continuing to the credit application from calculator
            link_url: window.location.href,
            product_name: this.getProductName(container),
            affiliation: undefined,
            item_id: vehicleConfig.vin,
            item_number: undefined,
            item_price: vehicleConfig.price,
            item_condition: undefined,
            item_year: vehicleConfig.year,
            item_make: vehicleConfig.make,
            item_model: vehicleConfig.model,
            item_variant: undefined,
            item_color: undefined,
            item_type: undefined,
            item_category: undefined,
            item_inventory_date: undefined,
            item_location: container.dealerName, // will only fill for multi-dealer setups
        });
    }
}